import {
  Grid,
  Heading,
  Skeleton,
  CardBody,
  Card,
  Button,
  Flex,
  VStack,
  Image,
  Avatar,
  useBreakpointValue,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Text,
  PopoverCloseButton,
} from '@chakra-ui/react';
import range from 'lodash/range';
import { memo, useState } from 'react';
import { T } from '@transifex/react';
import { env, marketplaceSystemIds } from 'features/common/config/envConfig';
import { useGetCustomAppTenantsStandaloneQuery } from 'gql/graphql';
import TruncatedText from 'features/common/components/TruncatedText';
import NoTenantsPanelMessage from './NoTenantsPanelMessage';
import { format } from 'date-fns';
import { useFeatures } from 'lib/featureFlags/features';

const MarketplaceAppsPanelContent = () => {
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const maxLength = useBreakpointValue({ base: 15, xl: 16 }) ?? 15;
  const features = useFeatures();

  const {
    data: tenantsQueryData,
    loading: tenantsQueryLoading,
    fetchMore,
  } = useGetCustomAppTenantsStandaloneQuery({
    variables: {
      first: 20,
      env,
      systemId: marketplaceSystemIds[window.location.hostname],
      state: 'Active',
    },
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-only',
  });

  const edges = tenantsQueryData?.user?.tenants?.edges ?? [];
  const nodes = tenantsQueryData?.user?.tenants?.nodes ?? [];

  // Function to get the app icon URL from annotations
  const getIconUrl = (node: any): string => {
    const iconAnnotation = node?.annotations?.find(
      (annotation: any) => annotation.key === 'IconUrl',
    );
    return iconAnnotation?.value;
  };

  // Function to get the app URL from annotations
  const getAppUrl = (node: any): string => {
    const urlAnnotation = node?.annotations?.find(
      (annotation: any) => annotation.key === 'URL',
    );
    return urlAnnotation?.value || '#';
  };

  // Function to handle app click - opens in a new tab
  const handleAppClick = (url: string, isSuspended: boolean) => {
    if (isSuspended) return; // Don't allow clicking suspended apps
    if (url && url !== '#') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Function to handle image load error
  const handleImageError = (nodeId: string) => {
    setFailedImages((prev) => {
      const newSet = new Set([...prev, nodeId]);
      return newSet;
    });
  };

  // Function to check if image should show fallback
  const shouldShowFallback = (node: any): boolean => {
    const iconUrl = getIconUrl(node);
    const hasFailed = failedImages.has(node.id);
    const hasNoIcon = !iconUrl || iconUrl.trim() === '';

    return hasFailed || hasNoIcon;
  };

  const fetchMoreTenants = () => {
    const { endCursor } = tenantsQueryData?.user?.tenants?.pageInfo || {};

    return fetchMore({
      variables: {
        after: endCursor,
      },
      updateQuery: (prev: any, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const pageInfo = fetchMoreResult?.user?.tenants?.pageInfo || {
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: null,
          endCursor: null,
        };

        return {
          user: {
            ...fetchMoreResult.user,
            tenants: {
              ...fetchMoreResult.user?.tenants,
              edges: [
                ...(prev?.user?.tenants?.edges ?? []),
                ...(fetchMoreResult?.user?.tenants?.edges ?? []),
              ],
              nodes: [
                ...(prev?.user?.tenants?.nodes ?? []),
                ...(fetchMoreResult?.user?.tenants?.nodes ?? []),
              ],
              pageInfo,
              totalCount: fetchMoreResult?.user?.tenants?.totalCount || 0,
            },
          },
        };
      },
    });
  };

  const shouldShowMoreButton =
    (!tenantsQueryLoading &&
      tenantsQueryData?.user?.tenants?.pageInfo?.hasNextPage) ??
    false;

  return (
    <>
      <Heading size='md' noOfLines={1}>
        Apps
      </Heading>
      <Card
        variant='flat'
        size={['md', 'lg']}
        w='full'
        data-testid='marketplace-apps-panel-block'
        data-behavior-analytics-feature='marketplace-apps-panel - home page'
      >
        <CardBody>
          {tenantsQueryLoading ? (
            <Grid
              gap='4'
              templateColumns='repeat(auto-fill, minmax(var(--sizes-40), 1fr))'
            >
              {range(5).map((_, index) => (
                <Skeleton
                  h='32'
                  w='40'
                  isLoaded={false}
                  key={index}
                  data-testid='marketplace-apps-panel-skeleton-placeholder'
                />
              ))}
            </Grid>
          ) : edges.length < 1 ? (
            <NoTenantsPanelMessage />
          ) : (
            <Grid
              columnGap='0'
              rowGap='4'
              templateColumns={[
                'repeat(auto-fill, minmax(var(--sizes-32), 1fr))',
                'repeat(auto-fill, minmax(var(--sizes-44), 1fr))',
              ]}
            >
              {nodes.map((node) => {
                const isSuspended = node.labels?.some(
                  (label) =>
                    label.key === 'Suspended' && label.value === 'true',
                );
                const scheduleDeletion = node.annotations?.some(
                  (item) =>
                    item.key === 'Deletion.IsPending' && item.value === 'true',
                );
                const scheduleDeletionDate = node.annotations?.find(
                  (item) => item.key === 'Deletion.ScheduledAt',
                )?.value;
                const deletionDate = scheduleDeletionDate
                  ? format(new Date(scheduleDeletionDate), 'dd MMMM yyyy')
                  : null;

                const appUrl = getAppUrl(node);
                const isSuspendedOrScheduledDeletion =
                  isSuspended || scheduleDeletion;

                return (
                  <Popover
                    key={node.id}
                    trigger={
                      isSuspendedOrScheduledDeletion ? 'hover' : undefined
                    }
                    isLazy
                  >
                    <PopoverTrigger>
                      <VStack
                        as='button'
                        type='button'
                        position='relative'
                        p='4'
                        aria-disabled={isSuspended || undefined}
                        onClick={
                          isSuspended
                            ? undefined
                            : () => handleAppClick(appUrl, isSuspended)
                        }
                        cursor={isSuspended ? 'not-allowed' : 'pointer'}
                        _hover={{
                          backgroundColor: isSuspendedOrScheduledDeletion
                            ? 'transparent'
                            : 'neutral.100',
                        }}
                        _active={{
                          backgroundColor: isSuspendedOrScheduledDeletion
                            ? 'transparent'
                            : 'neutral.200',
                        }}
                        borderRadius='md'
                        transitionProperty='common'
                        transitionDuration='normal'
                        height='100px'
                        alignItems='center'
                        justifyContent='center'
                        borderWidth={
                          isSuspendedOrScheduledDeletion ? '2px' : '0'
                        }
                        borderColor={
                          isSuspendedOrScheduledDeletion
                            ? 'red.100'
                            : 'transparent'
                        }
                        data-testid={`marketplace-app-tile-${node.id}`}
                      >
                        {isSuspendedOrScheduledDeletion && (
                          <Badge
                            position='absolute'
                            top='0'
                            right='0'
                            colorScheme='danger'
                            fontSize='2xs'
                            fontWeight='bold'
                            borderRadius='sm'
                            textTransform='none'
                          >
                            {isSuspended && 'Suspended'}
                            {scheduleDeletion && 'Scheduled Deletion'}
                          </Badge>
                        )}
                        {shouldShowFallback(node) ? (
                          <Avatar
                            boxSize='10'
                            name={node.displayName}
                            getInitials={(name) => name.charAt(0).toUpperCase()}
                            borderRadius='lg'
                          />
                        ) : (
                          <Image
                            boxSize='10'
                            src={getIconUrl(node)}
                            alt={node.displayName}
                            borderRadius='lg'
                            onError={() => handleImageError(node.id)}
                          />
                        )}
                        <TruncatedText
                          text={node.displayName}
                          maxLength={maxLength}
                          fontWeight='medium'
                          color='blackAlpha.900'
                          fontSize='md'
                          noOfLines={1}
                        />
                      </VStack>
                    </PopoverTrigger>
                    {isSuspendedOrScheduledDeletion && (
                      <PopoverContent>
                        <PopoverCloseButton />
                        <PopoverArrow />
                        <PopoverBody>
                          <VStack align='start' spacing='2'>
                            <Text fontWeight='bold'>
                              {isSuspended && 'App suspended'}
                              {scheduleDeletion && 'Deleted app'}
                            </Text>
                            <Text fontSize='sm'>
                              {isSuspended &&
                                `This app has been suspended and is no longer
                              available to use. Organization admins and owners
                              can uninstall them on the "My apps" page.`}

                              {scheduleDeletion &&
                                `This app is scheduled for deletion by the app creator and will stop working on ${deletionDate}.`}
                            </Text>
                          </VStack>
                        </PopoverBody>
                      </PopoverContent>
                    )}
                  </Popover>
                );
              })}
            </Grid>
          )}
          {shouldShowMoreButton && (
            <Flex align='center' justifyContent='center'>
              <Button
                variant='link'
                mt='6'
                onClick={fetchMoreTenants}
                data-testid='marketplace-apps-show-more-button'
              >
                <T _str='Show more' />
              </Button>
            </Flex>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export const MarketplaceAppsPanel = memo(MarketplaceAppsPanelContent);
